// SPARK brand mark — a refined 8-point compass "north star". Shaded the same
// soft way as the rest of the home illustrations: a smooth diagonal body
// gradient (white top-left → light indigo bottom-right) with a soft radial
// sheen, an extruded indigo base for 3D depth, and a gentle spotlight behind.
// No hard facets — dimension comes from smooth gradients, matching the
// domain/step illustration family.
//
// Geometry is computed once at module load from polar coordinates (deterministic
// — no Date/random — so it's SSR-safe).
//
// NOTE: this mirrors apps/web/src/components/brand/SparkStar.tsx. The admin app
// can't import across the app boundary, and the learner shell (AuthHeroLayout)
// is itself app-local rather than shared, so the admin dark-glass shell keeps
// its own copy of the mark. Keep the two in sync if the geometry changes.

const CX = 24;
const CY = 24;

interface Pt {
  x: number;
  y: number;
}

function buildVertices(): Pt[] {
  const rValley = 4.4;
  const rVert = 23; // N / S points (longest)
  const rHorz = 21.5; // E / W points
  const rDiag = 11.5; // diagonal points (shortest)
  const tipRadius = (i: number) => (i % 2 !== 0 ? rDiag : i % 4 === 0 ? rVert : rHorz);
  const polar = (deg: number, r: number): Pt => {
    const a = (deg * Math.PI) / 180; // 0° = up (North), clockwise
    return { x: CX + r * Math.sin(a), y: CY - r * Math.cos(a) };
  };
  const v: Pt[] = [];
  for (let i = 0; i < 8; i++) {
    v.push(polar(i * 45, tipRadius(i))); // tip
    v.push(polar(i * 45 + 22.5, rValley)); // valley
  }
  return v;
}

const V = buildVertices();
const STAR_FULL = `M ${V.map((p) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' L ')} Z`;

// Shading palettes. `brand` (indigo shadow) sits on the blue brand square/orb;
// `mono` (grey shadow) sits on the dark charcoal streak chips.
const TONES = {
  brand: { mid: '#DCE3FF', low: '#A5B4FC', base: '#4338CA' },
  mono: { mid: '#E2E8F0', low: '#CBD5E1', base: '#64748B' },
  // Warm white body + amber shadow — for sitting on a gold surface (e.g. the
  // program-complete medallion) so the extruded base reads as a gold shadow.
  gold: { mid: '#FFF8EC', low: '#FBEFD3', base: '#B45309' },
} as const;

export type SparkStarVariant = keyof typeof TONES;

export interface SparkStarProps {
  className?: string;
  /** Shadow/shading tone — `brand` (indigo) for blue backgrounds, `mono`
   *  (grey) for dark backgrounds. Defaults to `brand`. */
  variant?: SparkStarVariant;
}

export function SparkStar({ className, variant = 'brand' }: SparkStarProps) {
  const tone = TONES[variant];
  const bodyId = `spark-body-${variant}`;
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true" className={className}>
      <defs>
        {/* soft spotlight that grounds the mark; fully fades out (no hard ring) */}
        <radialGradient id="spark-glow" cx="50%" cy="46%" r="50%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.5" />
          <stop offset="48%" stopColor="#FFFFFF" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
        {/* smooth body shading: lit top-left → soft tone bottom-right */}
        <linearGradient id={bodyId} x1="0.16" y1="0.08" x2="0.84" y2="0.92">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="52%" stopColor={tone.mid} />
          <stop offset="100%" stopColor={tone.low} />
        </linearGradient>
        {/* soft top-left specular sheen layered over the body */}
        <radialGradient id="spark-sheen" cx="37%" cy="30%" r="46%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
      </defs>

      <circle cx="24" cy="24" r="24" fill="url(#spark-glow)" />

      {/* extruded base nudged down for soft 3D thickness */}
      <g transform="translate(0 1.6)">
        <path d={STAR_FULL} fill={tone.base} />
      </g>

      {/* smoothly shaded body + soft sheen */}
      <path
        d={STAR_FULL}
        fill={`url(#${bodyId})`}
        stroke={`url(#${bodyId})`}
        strokeWidth="1.1"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path d={STAR_FULL} fill="url(#spark-sheen)" />
    </svg>
  );
}
